<template>
    <VAppLayout>
        <VPageHeader :title="$t('pages.menu.favorites.title')" back @back="goBack">
            <template #trailing>
                <button v-if="favorites.length"
                        type="button"
                        class="favorites-clear"
                        @click="clearAll">
                    {{ $t('pages.menu.favorites.clearAll') }}
                </button>
            </template>
        </VPageHeader>

        <VContainer>
            <div class="favorites">
                <ul v-if="favorites.length" class="favorites__list">
                    <MenuItem v-for="item in favorites"
                              :item="item"
                              :key="item.id" />
                </ul>

                <div v-else class="favorites-empty">
                    <span class="favorites-empty__icon">
                        <IconStarOutline width="32" height="32" />
                    </span>
                    <div class="favorites-empty__title">{{ $t('pages.menu.favorites.emptyTitle') }}</div>
                    <div class="favorites-empty__sub">{{ $t('pages.menu.favorites.emptySub') }}</div>
                    <router-link :to="{ name: 'menu' }" class="favorites-empty__cta">
                        {{ $t('pages.menu.favorites.browseMenu') }}
                    </router-link>
                </div>
            </div>
        </VContainer>
    </VAppLayout>
</template>

<script>
import VAppLayout from "@/layouts/VAppLayout.vue";
import VContainer from "@/components/ui/VContainer.vue";
import VPageHeader from "@/components/ui/VPageHeader.vue";
import MenuItem from "./components/MenuItem.vue";
import IconStarOutline from "@/components/icons/IconStarOutline.vue";

export default {
    name: "MenuFavorites",
    components: {
        VAppLayout,
        VContainer,
        VPageHeader,
        MenuItem,
        IconStarOutline,
    },
    data() {
        return {
            toastOptions: {
                position: "top-center",
                timeout: 3000,
                closeOnClick: true
            },
        };
    },
    computed: {
        favorites() {
            return this.$store.getters['favoritesStore/getFavorites'] || [];
        },
    },
    methods: {
        goBack() {
            this.$router.back();
        },
        clearAll() {
            this.$store.commit('favoritesStore/CLEAR_FAVORITES');
            this.$toast.success(this.$t('pages.menu.favorites.cleared'), this.toastOptions);
        },
    }
};
</script>

<style lang="scss" scoped>
.favorites {
    padding-top: 8px;
    /* clear the floating bottom nav (content overflows VContainer, so the layout
       reserve is bypassed — carry the clearance here) */
    padding-bottom: calc(var(--nav-height) + var(--nav-float-gap) * 2 + var(--safe-bottom) + 24px);
}

.favorites__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;

    /* MenuItem's own vertical margins don't collapse inside a flex column, so
       they'd stack on top of the gap. Zero them here and let gap be the single
       source of spacing between cards. */
    ::v-deep .menu-item-li {
        margin: 0;
    }
}

.favorites-clear {
    padding: 8px 14px;

    font-family: var(--font-sans);
    font-weight: var(--weight-bold);
    font-size: 13px;
    line-height: 1;

    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-secondary);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    transition: transform var(--dur-fast) var(--ease-standard);

    &:active { transform: scale(0.94); }
}

.favorites-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;

    padding: 64px 24px;
}

.favorites-empty__icon {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 72px;
    height: 72px;

    border-radius: 50%;

    background: var(--fav-star-soft);
    color: var(--fav-star);
}

.favorites-empty__title {
    font-size: 18px;
    font-weight: var(--weight-bold);
    color: var(--text-primary);
}

.favorites-empty__sub {
    max-width: 260px;

    font-size: 14px;
    line-height: 1.4;
    color: var(--text-secondary);
}

.favorites-empty__cta {
    margin-top: 6px;
    padding: 11px 22px;

    font-weight: var(--weight-bold);
    font-size: 14px;
    line-height: 1;

    border-radius: var(--radius-pill);
    background: var(--grad-brand);
    box-shadow: var(--shadow-brand);
    color: var(--color-on-primary);
    text-decoration: none;
}
</style>
